// ============================================================
// REFERENCE CHECK FORM — Fix #6: loads existing reference and UPDATES it.
// ============================================================
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Check } from 'lucide-react';
import { api, REFERENCE_QUESTIONS, REF_RESULTS, fmtSaved } from './constants';

export function ReferenceForm({ candidateId, navigate }) {
  const [form, setForm] = useState({ candidate_id: candidateId, referee_name: '', referee_phone: '', referee_email: '', relationship_to_candidate: '', reference_completed: false, reliability_notes: '', stress_response_notes: '', feedback_response_notes: '', mistake_ownership_notes: '', trust_notes: '', client_care_notes: '', supervision_notes: '', would_rehire: null, concerns: '', reference_result: '' });
  const [existingId, setExistingId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api(`/api/recruitment/candidates/${candidateId}`).then(c => {
      if (!active) return;
      const r = c.reference_checks && c.reference_checks[0];
      if (r) {
        setForm(prev => ({ ...prev, ...r, candidate_id: candidateId }));
        setExistingId(r.id);
        setSavedAt(r.updated_at || r.created_at);
      }
    }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [candidateId]);

  const save = async () => {
    if (!existingId && !form.referee_name.trim()) { toast.error('Referee name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (existingId) {
        const res = await api(`/api/recruitment/references/${existingId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        setSavedAt(res.updated_at || new Date().toISOString());
      } else {
        const res = await api('/api/recruitment/references', { method: 'POST', body: JSON.stringify(payload) });
        setExistingId(res.id);
        setSavedAt(res.updated_at || res.created_at || new Date().toISOString());
      }
      toast.success('Reference saved');
      navigate('candidate', candidateId);
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const F = (label, key) => <div className="rec-field"><label className="rec-label">{label}</label><textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="rec-input rec-textarea" rows={2} /></div>;

  if (loading) return <div className="h-40 bg-muted rounded-2xl animate-skeleton" />;

  return (
    <div data-testid="reference-form">
      <button className="ch-back" onClick={() => navigate('candidate', candidateId)}><ArrowLeft size={16} /> Back</button>
      <h1 className="text-xl font-bold mb-1">Reference Check</h1>
      {savedAt && <p className="text-xs text-muted-foreground mb-5 flex items-center gap-1"><Check size={12} className="text-[hsl(var(--success))]" /> Last saved {fmtSaved(savedAt)}</p>}
      {!savedAt && <div className="mb-5" />}
      <div className="ps-card-static p-6 mb-6">
        <h3 className="text-sm font-semibold mb-4">Referee Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rec-field"><label className="rec-label">Name</label><input value={form.referee_name} onChange={e => setForm(f => ({ ...f, referee_name: e.target.value }))} className="rec-input" data-testid="referee-name" /></div>
          <div className="rec-field"><label className="rec-label">Relationship</label><input value={form.relationship_to_candidate} onChange={e => setForm(f => ({ ...f, relationship_to_candidate: e.target.value }))} className="rec-input" /></div>
          <div className="rec-field"><label className="rec-label">Phone</label><input value={form.referee_phone} onChange={e => setForm(f => ({ ...f, referee_phone: e.target.value }))} className="rec-input" /></div>
          <div className="rec-field"><label className="rec-label">Email</label><input value={form.referee_email} onChange={e => setForm(f => ({ ...f, referee_email: e.target.value }))} className="rec-input" /></div>
        </div>
      </div>
      <div className="ps-card-static p-6">
        <h3 className="text-sm font-semibold mb-4">Reference Questions</h3>
        {REFERENCE_QUESTIONS.map(q => <React.Fragment key={q.key}>{F(q.label, q.key)}</React.Fragment>)}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rec-field"><label className="rec-label">Would rehire?</label><select value={form.would_rehire === null ? '' : form.would_rehire ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, would_rehire: e.target.value === 'yes' }))} className="rec-input"><option value="">Select...</option><option value="yes">Yes</option><option value="no">No</option></select></div>
          <div className="rec-field"><label className="rec-label">Result</label><select value={form.reference_result} onChange={e => setForm(f => ({ ...f, reference_result: e.target.value }))} className="rec-input"><option value="">Select...</option>{REF_RESULTS.map(r => <option key={r}>{r}</option>)}</select></div>
        </div>
        <button onClick={save} disabled={saving} className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }} data-testid="reference-save-btn">{saving ? 'Saving...' : 'Save Reference'}</button>
      </div>
    </div>
  );
}
